import { Link } from 'react-router-dom';
import { Compass } from 'lucide-react';
import { brand } from '@/lib/theme/tokens';

/* ─── Desktop columns — nothing that lives in the navbar ──────────────────── */
const COLUMNS = [
  {
    title: 'Discover',
    links: [
      { label: 'Houseboats', to: '/houseboats' },
      { label: 'Destinations', to: '/destinations' },
      { label: 'Seasons', to: '/seasons' },
      { label: 'Stories', to: '/stories' },
      { label: 'Rewards', to: '/rewards' },
    ],
  },
  {
    title: 'Community',
    links: [
      { label: 'Trip planner', to: '/dashboard/trip-planner' },
      { label: 'Clubs', to: '/clubs' },
      { label: 'Find companions', to: '/community/matches' },
      { label: 'Leaderboard', to: '/clubs/leaderboard' },
      { label: 'Write a review', to: '/reviews/write' },
    ],
  },
  {
    title: 'Business',
    links: [
      { label: 'For operators', to: '/business/for-operators' },
      { label: 'For hotels', to: '/business/for-hotels' },
      { label: 'For restaurants', to: '/business/for-restaurants' },
      { label: 'AI agency', to: '/business/ai-agency' },
      { label: 'Pricing', to: '/business/pricing' },
    ],
  },
  {
    title: 'Support',
    links: [
      { label: 'Help center', to: '/help' },
      { label: 'Cancellation policy', to: '/help/cancellation' },
      { label: 'Booking lookup', to: '/booking-lookup' },
      { label: 'Safety', to: '/safety' },
      { label: 'Press', to: '/press' },
    ],
  },
];

/* ─── Mobile strip — only links not reachable from the navbar ─────────────── */
const MOBILE_LINKS = [
  { label: 'Help', to: '/help' },
  { label: 'Cancellation', to: '/help/cancellation' },
  { label: 'Safety', to: '/safety' },
  { label: 'Press', to: '/press' },
  { label: 'Contact', to: '/business/contact' },
  { label: 'Pricing', to: '/business/pricing' },
];

export function Footer() {
  const year = new Date().getFullYear();

  return (
    <footer className="mt-24 border-t border-[var(--glass-edge)]">

      {/* ── Desktop footer ─────────────────────────────────────────────────── */}
      <div className="hidden md:block">
        <div className="mx-auto max-w-7xl px-6 py-14">
          <div className="grid grid-cols-[1.8fr_1fr_1fr_1fr_1fr] gap-10">

            {/* Brand column */}
            <div className="flex flex-col gap-5">
              <Link to="/" className="flex items-center gap-2.5">
                <div
                  className="grid h-9 w-9 shrink-0 place-items-center rounded-[10px]"
                  style={{
                    background: `linear-gradient(135deg, ${brand.teal}, ${brand.tealDark})`,
                    boxShadow: `0 0 20px ${brand.teal}50`,
                  }}
                >
                  <Compass className="h-4 w-4 text-white" strokeWidth={2} />
                </div>
                <div className="font-display text-[15px] font-bold tracking-tight text-ink">
                  Uncharted Odyssey
                </div>
              </Link>

              <p className="text-[12.5px] leading-relaxed text-ink-dim max-w-[220px]">
                Kashmir's adventure marketplace. Every trail, every stay — curated by people who actually live here.
              </p>

              <div className="flex flex-col gap-1.5 text-[11.5px] text-ink-mute">
                <span
                  className="inline-flex items-center gap-1.5 font-semibold"
                  style={{ color: brand.teal }}
                >
                  <span
                    className="inline-block h-1.5 w-1.5 rounded-full animate-pulse"
                    style={{ background: brand.teal }}
                  />
                  8% of every booking funds trail cleanup
                </span>
                <span>Headquartered in Srinagar, Kashmir</span>
              </div>
            </div>

            {/* Link columns */}
            {COLUMNS.map((col) => (
              <div key={col.title}>
                <div className="mb-4 text-[10px] font-bold uppercase tracking-[0.22em] text-ink-mute">
                  {col.title}
                </div>
                <ul className="flex flex-col gap-2.5">
                  {col.links.map((l) => (
                    <li key={l.label}>
                      <Link
                        to={l.to}
                        className="text-[12.5px] text-ink-dim transition-colors hover:text-ink"
                      >
                        {l.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Desktop bottom strip */}
        <div
          className="border-t border-[var(--glass-edge)] px-6 py-4"
        >
          <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 text-[11px] text-ink-mute">
            <span>© {year} Uncharted Odyssey Pvt. Ltd.</span>
            <div className="flex items-center gap-4">
              <Link to="/help" className="hover:text-ink transition-colors">Privacy</Link>
              <Link to="/help" className="hover:text-ink transition-colors">Terms</Link>
              <Link to="/business/contact" className="hover:text-ink transition-colors">Contact</Link>
              <span
                className="inline-flex items-center gap-1 font-medium"
                style={{ color: brand.teal }}
              >
                Crafted in Srinagar
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Mobile footer — compact, no navbar duplication ─────────────────── */}
      <div className="md:hidden px-5 py-8">

        {/* Brand */}
        <div className="flex items-center gap-2.5 mb-3">
          <div
            className="grid h-7 w-7 shrink-0 place-items-center rounded-[8px]"
            style={{
              background: `linear-gradient(135deg, ${brand.teal}, ${brand.tealDark})`,
            }}
          >
            <Compass className="h-3.5 w-3.5 text-white" strokeWidth={2} />
          </div>
          <span className="font-display text-[14px] font-bold tracking-tight text-ink">
            Uncharted Odyssey
          </span>
        </div>
        <p className="text-[11.5px] leading-relaxed text-ink-mute mb-5">
          Kashmir's adventure marketplace. Curated by locals.
        </p>

        {/* Compact link strip */}
        <div className="flex flex-wrap gap-x-4 gap-y-2 mb-6">
          {MOBILE_LINKS.map((l) => (
            <Link
              key={l.label}
              to={l.to}
              className="text-[12px] text-ink-dim hover:text-ink transition-colors"
            >
              {l.label}
            </Link>
          ))}
        </div>

        {/* Copyright */}
        <div className="flex items-center justify-between text-[10.5px] text-ink-mute border-t border-[var(--glass-edge)] pt-4">
          <span>© {year} Uncharted Odyssey</span>
          <span style={{ color: brand.teal }} className="font-medium">Srinagar, Kashmir</span>
        </div>
      </div>

    </footer>
  );
}
